// Visually-hidden aria-live region that announces which products are currently
// auto-included (brought along free by a selected product). Screen-reader users
// don't see the "Included" chips/strips, so when selecting a product silently
// flips other product cards into an included state, this politely narrates it.
//
// Rendered once per intake form. It owns no UI footprint — it's an sr-only span
// whose text content changes as the included set changes, which assistive tech
// reads out via aria-live="polite".
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Box } from '@mui/material';
import { groupIncludesByParent, computeIncludedProducts } from '../../utils/product-includes.js';

const EMPTY = [];

const IncludedProductsAnnouncer = () => {
  const items = useSelector((s) => s.selections.items);
  const includeRows = useSelector((s) => s.catalog.data?.includes ?? EMPTY);
  const products = useSelector((s) => s.catalog.data?.products ?? EMPTY);

  const message = useMemo(() => {
    if (!includeRows.length) return '';
    const grouped = groupIncludesByParent(includeRows);
    const selectedIds = items.map((i) => i.productId);
    const includedMap = computeIncludedProducts(grouped, selectedIds);
    if (!includedMap.size) return '';
    const nameById = new Map(products.map((p) => [p.id, p.name]));
    const names = [...includedMap.keys()]
      .map((id) => nameById.get(id))
      .filter(Boolean);
    if (!names.length) return '';
    const list = names.join(', ');
    return names.length === 1
      ? `${list} is included at no charge with your current selection.`
      : `${list} are included at no charge with your current selection.`;
  }, [includeRows, items, products]);

  return (
    <Box
      component="span"
      aria-live="polite"
      sx={{
        position: 'absolute', left: '-9999px', width: '1px', height: '1px', overflow: 'hidden',
      }}
    >
      {message}
    </Box>
  );
};

export default IncludedProductsAnnouncer;
